import { RetweetOutlined } from '@ant-design/icons';
import Icon from '@ant-design/icons/lib/components/Icon';
import { useTranslate } from '@tolgee/react';
import { useMount } from 'ahooks';
import { Button, Input, Tag, message } from 'antd';
import useCrud from 'hooks/useCrud';
import { useCallback, useMemo, useRef, useState } from 'react';
import { ReactComponent as MedalSvg } from '../assets/medal.svg';
import styled from '@emotion/styled';
import { permissionNameIdMap } from 'hooks/business/useNewPermisson';
import { getAdminStores } from 'services/api/admin';
import { mercadoReputation } from 'services/api/dashboard';
import { formatNumber } from 'utils/numberFormat';

const claimConfig = [
  [
    [
      [0.01, 0.02],
      [0.02, 0.02],
    ],
    [[0.005, 0.01], []],
    [
      [0.005, 0.02],
      [0.01, 0.03],
    ],
    [
      [0.08, 0.15],
      [0.1, 0.15],
    ],
  ],
  [
    [
      [0.01, 0.03],
      [0.02, 0.03],
    ],
    [[0.005, 0.01], []],
    [
      [0.005, 0.02],
      [0.015, 0.03],
    ],
    [
      [0.06, 0.15],
      [0.1, 0.15],
    ],
  ],
  [
    [[0.01, 0.04], [0.02]],
    [[0.005, 0.01], []],
    [[0.005, 0.02], [0.01]],
    [[0.08, 0.15], [0.1]],
  ],
];

const getTarget = (
  region: General.UpperCaseRegion,
  type: 0 | 1 | 2 | 3,
  isLeader: boolean,
  storeType: 'LOCAL' | 'CROSS',
) => {
  const regionIndexMap: Partial<Record<General.UpperCaseRegion, number>> = {
    MX: 0,
    BR: 1,
    AR: 2,
  };

  const regionIndex = regionIndexMap[region];

  if (regionIndex === undefined) {
    return 1;
  }

  return claimConfig[regionIndex][type][isLeader ? 0 : 1][storeType === 'LOCAL' ? 0 : 1] || 1;
};

function Reputation() {
  const { t } = useTranslate();

  const refreshTimeRef = useRef(0);

  const { list, loading, fetchList } = useCrud({
    readList: useCallback(() => {
      return getAdminStores({
        externalType: 'MERCADO',
        page: 1,
        pageSize: 99999,
        permId: permissionNameIdMap['履约数据'],
      })
        .then((res) => {
          return mercadoReputation({
            storeIds: res.data.data.stores.map(({ id }) => id),
          });
        })
        .then((res) => {
          refreshTimeRef.current = Date.now();
          return res.data;
        });
    }, []),
  });

  const [keyword, setKeyword] = useState('');

  useMount(() => {
    fetchList().catch(() => {
      message.error(t('phrase.获取数据失败，请稍后再试'));
    });
  });

  const displayList = useMemo(() => {
    return list?.filter(
      ({ storeName, storeAlias }) => storeName.includes(keyword) || storeAlias.includes(keyword),
    );
  }, [list, keyword]);

  return (
    <Container>
      <header>
        <h3>{t('店铺声誉')}</h3>
        <Button
          loading={loading}
          type="text"
          icon={<RetweetOutlined />}
          onClick={() => {
            if (Date.now() - refreshTimeRef.current <= 60 * 1000) {
              message.warn(t('已是最新数据'));
            } else {
              fetchList().catch(() => {
                message.error(t('phrase.获取数据失败，请稍后再试'));
              });
            }
          }}
        >
          {t('刷新实时数据')}
        </Button>
      </header>
      <section>
        <div>
          <Input.Search
            allowClear
            placeholder={t('department.输入店铺名称/别名')}
            onSearch={setKeyword}
          />
        </div>
        <ul>
          {displayList?.map(
            ({
              storeName,
              storeAlias,
              storeType,
              region,
              level,
              sellerStatus,
              // sales,
              claims,
              delayedDandling,
              cancellations,
            }) => {
              const storeLevel = parseInt(level, 10) - 1;
              const status = {
                platinum: 0,
                gold: 1,
                silver: 2,
                '': 3,
              }[sellerStatus];
              return (
                <li>
                  <StoreInfo>
                    <h4>
                      <div>{storeAlias || storeName}</div>
                      {storeType === 'LOCAL' ? (
                        <Tag color="blue">{t('本土')}</Tag>
                      ) : (
                        <Tag color="green">{t('跨境')}</Tag>
                      )}
                    </h4>
                    <div style={{ color: ['#019860', '#F2A100', '#515359', '#515359'][status] }}>
                      <Icon component={MedalSvg} />
                      {
                        ['MercadoLíder Platinum', 'MercadoLíder Gold', 'MercadoLíder', t('无等级')][
                          status
                        ]
                      }
                    </div>
                    <ul>
                      {['#F9605B', '#FCB657', '#FEEF44', '#B9F921', '#32B93D'].map(
                        (color, index) => {
                          return (
                            <li
                              style={{
                                backgroundColor: storeLevel >= index ? color : '#F3F3F3',
                                opacity: storeLevel > index ? 0.2 : 1,
                              }}
                            >
                              {index === storeLevel && (
                                <span style={{ borderBottomColor: color }} />
                              )}
                            </li>
                          );
                        },
                      )}
                    </ul>
                  </StoreInfo>
                  <section>
                    <dl>
                      <dt>{t('投诉订单')}</dt>
                      <dd
                        style={
                          claims.rate > getTarget(region, 0, Boolean(sellerStatus), storeType)
                            ? { color: '#F05B5B' }
                            : undefined
                        }
                      >
                        {formatNumber(claims.rate, '0.00%')}
                      </dd>
                    </dl>
                    {/* <dl>
                      <dt>{t('调解订单')}</dt>
                      <dd
                        style={
                          claims.rate > getTarget(region, 1, Boolean(sellerStatus), storeType)
                            ? { color: '#F05B5B' }
                            : undefined
                        }
                      >
                        {formatNumber(claims.rate, '0.00%')}
                      </dd>
                    </dl> */}
                    <dl>
                      <dt>{t('mercado取消订单')}</dt>
                      <dd
                        style={
                          cancellations.rate >
                          getTarget(region, 2, Boolean(sellerStatus), storeType)
                            ? { color: '#F05B5B' }
                            : undefined
                        }
                      >
                        {formatNumber(cancellations.rate, '0.00%')}
                      </dd>
                    </dl>
                    <dl>
                      <dt>{t('迟发订单')}</dt>
                      <dd
                        style={
                          delayedDandling.rate >
                          getTarget(region, 3, Boolean(sellerStatus), storeType)
                            ? { color: '#F05B5B' }
                            : undefined
                        }
                      >
                        {formatNumber(delayedDandling.rate, '0.00%')}
                      </dd>
                    </dl>
                  </section>
                </li>
              );
            },
          )}
        </ul>
      </section>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding-top: 16px;
  > header {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 56px;
    padding: 0 24px;
    background: #fff;
    margin-bottom: 1px;
    > h3 {
      margin-bottom: 0;
    }
  }
  > section {
    flex: 1 0 0;
    padding: 16px;
    background: #fff;
    overflow: auto;
    > div {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 12px;
      > * {
        width: 240px;
      }
    }
    > ul {
      margin: 0;
      padding: 0;
      list-style: none;
      > li {
        display: flex;
        align-items: stretch;
        margin-bottom: 12px;
        border: solid 1px #f0f2f5;
        border-radius: 2px;
        > div {
          flex: 4 0 0;
          padding: 14px 16px;
          border-right: solid 1px #f0f2f5;
          overflow: hidden;
        }
        > section {
          flex: 6 0 0;
          display: flex;
          align-items: stretch;
          > dl {
            flex: 1 0 0;
            padding: 16px;
            margin-bottom: 0;
            & + dl {
              border-left: solid 1px #f0f2f5;
            }
            > dt {
              margin-bottom: 8px;
              font-weight: 400;
              color: #84868c;
            }
            > dd {
              margin-bottom: 0;
              font-size: 18px;
            }
          }
        }
      }
    }
  }
`;

const StoreInfo = styled.div`
  > h4 {
    display: flex;
    align-items: center;
    > div {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    > span {
      margin-left: 4px;
      background: transparent;
    }
  }
  > div {
    margin-bottom: 8px;
  }
  > ul {
    display: flex;
    margin-bottom: 4px;
    padding: 0;
    list-style: none;
    gap: 4px;
    > li {
      position: relative;
      width: 48px;
      height: 10px;
      > span {
        position: absolute;
        top: 3px;
        left: 16px;
        border: solid 8px transparent;
      }
    }
  }
`;

export default Reputation;
